import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Badge, Button, Card } from '../components/ui';
import { clinicianService, encouragementService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ClinicianMessages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedScriptureId, setSelectedScriptureId] = useState('');
  const [streakCount, setStreakCount] = useState('');
  const [dayNumber, setDayNumber] = useState('');
  const [timePeriod, setTimePeriod] = useState('');
  const [eligibility, setEligibility] = useState({ canSend: null, reason: null, nextAvailableDate: null });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsResponse, templatesResponse, sentResponse] = await Promise.all([
          clinicianService.getClients(),
          encouragementService.getTemplates(),
          encouragementService.getSent({ limit: 20 })
        ]);

        setClients(clientsResponse.data?.data?.clients || []);
        setTemplates(templatesResponse.data?.templates || []);
        setSentMessages(sentResponse.data?.messages || []);
      } catch (err) {
        console.error('Failed to load encouragement messaging data:', err);
        setError('Unable to load messaging data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (clients.length === 0 && templates.length === 0) return;
    const clientIdParam = searchParams.get('clientId');
    const templateIdParam = searchParams.get('templateId');
    const dayNumberParam = searchParams.get('dayNumber');
    const timePeriodParam = searchParams.get('timePeriod');

    if (clientIdParam) {
      const exists = clients.some((client) => (client._id || client.id) === clientIdParam);
      if (exists) setSelectedClientId(clientIdParam);
    }

    if (templateIdParam) {
      const exists = templates.some((template) => template.templateId === templateIdParam);
      if (exists) setSelectedTemplateId(templateIdParam);
    }

    if (dayNumberParam) {
      setDayNumber(dayNumberParam);
    }

    if (timePeriodParam) {
      setTimePeriod(timePeriodParam);
    }
  }, [clients, templates, searchParams]);

  useEffect(() => {
    const checkEligibility = async () => {
      if (!selectedClientId) {
        setEligibility({ canSend: null, reason: null, nextAvailableDate: null });
        return;
      }

      try {
        const response = await encouragementService.checkEligibility(selectedClientId);
        setEligibility(response.data || { canSend: false, reason: 'Unable to verify eligibility' });
      } catch (err) {
        console.error('Failed to check eligibility:', err);
        setEligibility({ canSend: false, reason: 'Unable to verify eligibility' });
      }
    };

    checkEligibility();
  }, [selectedClientId]);

  useEffect(() => {
    setSelectedScriptureId('');
  }, [selectedClientId, selectedTemplateId]);

  const selectedTemplate = useMemo(() => {
    return templates.find((template) => template.templateId === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  const selectedClient = useMemo(() => {
    return clients.find((client) => (client._id || client.id) === selectedClientId) || null;
  }, [clients, selectedClientId]);

  const scriptureOptions = useMemo(() => {
    if (user?.role !== 'clergy') return [];
    if (!selectedTemplate?.scriptureOptions) return [];
    if (!selectedClient?.faithTrackEnabled) return [];
    return selectedTemplate.scriptureOptions;
  }, [selectedTemplate, selectedClient, user?.role]);

  const previewText = useMemo(() => {
    if (!selectedTemplate) return '';

    let text = selectedTemplate.messageText;
    if (streakCount) {
      text = text.replace(/\[Number\]/g, streakCount);
      text = text.replace(/\[Current number\]/g, streakCount);
    }
    if (dayNumber) {
      text = text.replace(/\[Number\]/g, dayNumber);
      text = text.replace(/\[Day number\]/g, dayNumber);
    }
    if (timePeriod) {
      text = text.replace(/\[Time period\]/g, timePeriod);
    }
    return text;
  }, [selectedTemplate, streakCount, dayNumber, timePeriod]);

  const selectedScripture = useMemo(() => {
    return scriptureOptions.find((option) => option.id === selectedScriptureId) || null;
  }, [scriptureOptions, selectedScriptureId]);

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedClientId || !selectedTemplateId) {
      setError('Please select a participant and a message template.');
      return;
    }

    if (eligibility?.canSend === false) {
      setError(eligibility?.reason || 'You cannot send a message to this participant right now.');
      return;
    }

    setSending(true);

    try {
      const triggerData = {};
      if (streakCount) triggerData.streakCount = Number(streakCount);
      if (dayNumber) triggerData.dayNumber = Number(dayNumber);
      if (timePeriod) triggerData.timePeriod = timePeriod;

      const response = await encouragementService.sendMessage({
        clientId: selectedClientId,
        templateId: selectedTemplateId,
        triggerData,
        scriptureOptionId: selectedScriptureId || undefined
      });

      setSuccess('Message sent successfully.');
      setEligibility({ canSend: false, reason: 'Message already sent this week.' });
      setSentMessages((prev) => [response.data.encouragementMessage, ...prev]);
      setSelectedTemplateId('');
      setSelectedScriptureId('');
      setStreakCount('');
      setDayNumber('');
      setTimePeriod('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <Badge variant="primary" size="sm">Encouragement Messages</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            {user?.role === 'clergy'
              ? 'Clergy Messaging'
              : user?.role === 'coach'
                ? 'Coach Messaging'
                : 'Clinician Messaging'}
          </h2>
          <p className="mt-2 text-neutral-600">
            Send template-based encouragement messages. One per participant per week.
          </p>
        </div>

        {/* Messaging Guidance */}
        <div className="mb-6 rounded-lg border-2 border-primary-200 bg-primary-50 p-5">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-primary-900">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Messaging Guidance
          </h3>
          <div className="mt-3 space-y-2 text-sm text-primary-800">
            <p className="font-medium">
              Focus your encouragement messages on:
            </p>
            <ul className="ml-5 space-y-1 list-disc">
              <li><strong>Platform engagement:</strong> Encourage daily login and content completion</li>
              <li><strong>Consistency:</strong> Emphasize the mental health benefits of regular practice</li>
              <li><strong>Observable progress:</strong> Acknowledge streaks, milestones, and phase transitions</li>
              <li><strong>Long-term commitment:</strong> Reinforce that consistent use leads to lasting mental health gains</li>
            </ul>
            <p className="mt-3 text-xs text-primary-700 italic">
              💡 Remember: These messages are about motivating platform use, not clinical interventions. 
              Encourage participants to build the habit of daily engagement for maximum therapeutic benefit.
            </p>
          </div>
        </div>

        <Card className="mb-8">
          <div className="flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-neutral-700">Select Participant</label>
                <select
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  <option value="">Choose a participant</option>
                  {clients.map((client) => {
                    const clientId = client._id || client.id;
                    return (
                      <option key={clientId} value={clientId}>
                        {client.firstName} {client.lastName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-700">Select Template</label>
                <select
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  <option value="">Choose a template</option>
                  {templates.map((template) => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.templateId} - {template.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-neutral-700">Streak Count (optional)</label>
                <input
                  type="number"
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={streakCount}
                  onChange={(e) => setStreakCount(e.target.value)}
                  placeholder="e.g., 7"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700">Day Number (optional)</label>
                <input
                  type="number"
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={dayNumber}
                  onChange={(e) => setDayNumber(e.target.value)}
                  placeholder="e.g., 30"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700">Time Period (optional)</label>
                <input
                  type="text"
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value)}
                  placeholder="e.g., 30 days"
                />
              </div>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-sm font-medium text-neutral-700">Preview</p>
              <p className="mt-2 text-sm text-neutral-800">
                {previewText || 'Select a template to preview the message.'}
              </p>
              {selectedScripture && (
                <p className="mt-3 text-xs text-neutral-600">
                  Scripture: {selectedScripture.reference} — {selectedScripture.text}
                </p>
              )}
            </div>

            {scriptureOptions.length > 0 && (
              <div>
                <label className="text-sm font-medium text-neutral-700">Optional Scripture Pairing</label>
                <select
                  className="mt-2 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                  value={selectedScriptureId}
                  onChange={(e) => setSelectedScriptureId(e.target.value)}
                >
                  <option value="">No scripture selected</option>
                  {scriptureOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.reference} — {option.text}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {eligibility?.canSend === false && selectedClientId && (
              <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
                {eligibility.reason || 'You have already sent a message to this participant this week.'}
                {eligibility.nextAvailableDate && (
                  <div className="mt-1 text-xs">
                    Next available: {new Date(eligibility.nextAvailableDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-700">
                {success}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={loading || sending || eligibility?.canSend === false}
              >
                {sending ? 'Sending...' : 'Send Encouragement'}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Recent Messages</h3>
            <Badge variant="secondary">{sentMessages.length}</Badge>
          </div>

          <div className="mt-4 space-y-4">
            {sentMessages.map((message) => (
              <div key={message._id} className="rounded-lg border border-neutral-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-neutral-900">
                    {message.clientId?.firstName} {message.clientId?.lastName}
                  </p>
                  <span className="text-xs text-neutral-500">
                    {message.sentAt ? new Date(message.sentAt).toLocaleDateString() : 'Unknown date'}
                  </span>
                </div>
                <p className="mt-2 text-sm text-neutral-700">{message.messageText}</p>
                {(message.scriptureReference || message.scriptureText) && (
                  <p className="mt-2 text-xs text-neutral-500">
                    Scripture: {message.scriptureReference || '—'} — {message.scriptureText || '—'}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-neutral-500">
                  <span className="rounded-full bg-neutral-100 px-2 py-1">Template {message.templateId}</span>
                  <span className="rounded-full bg-neutral-100 px-2 py-1">{message.templateCategory}</span>
                  <span className="rounded-full bg-neutral-100 px-2 py-1">{message.status}</span>
                </div>
              </div>
            ))}
            {sentMessages.length === 0 && (
              <p className="text-sm text-neutral-500">No messages sent yet.</p>
            )}
          </div>
        </Card>
      </main>
    </>
  );
};

export default ClinicianMessages;
